/**
 * Task Types
 * Core business entity with FSM (Finite State Machine) states
 * FSM v2.0 - 6 estados principales + 23 sub-estados
 */

import type { TaskType } from '@/lib/constants/task-types';

// ============================================
// ESTADOS PRINCIPALES FSM (6 estados)
// ============================================
export enum TaskStatus {
  VERIFICACION = 'verificacion',
  EN_PROCESO = 'en_proceso',
  DETENIDOS = 'detenidos',
  PAGADO = 'pagado',
  NEGADOS = 'negados',
  CANCELADO = 'cancelado',
}

// ============================================
// SUB-ESTADOS FSM (23 sub-estados)
// ============================================
export enum TaskSubStatus {
  // VERIFICACION (6 - added FACTURA_SOLICITADA 2025-01-12)
  VERIFICACION = 'verificacion',
  FACTURA_SOLICITADA = 'factura_solicitada',
  FACTURA_PENDIENTE = 'factura_pendiente',
  FACTURADA = 'facturada',
  REINGRESAR = 'reingresar',
  CSD_VENCIDO = 'csd_vencido',

  // EN_PROCESO (2 - added CONFIRMAR_PAGO_DOCTOR 2026-03-12)
  INGRESADA = 'ingresada',
  CONFIRMAR_PAGO_DOCTOR = 'confirmar_pago_doctor',

  // DETENIDOS (10 - added CAMBIO_DE_CUENTA 2026-02-12)
  DOCUMENTOS_VENCIDOS = 'documentos_vencidos',
  ALTA = 'alta',
  CARTA_PODER = 'carta_poder',
  NOTA_MEDICA = 'nota_medica',
  EN_ACLARACION = 'en_aclaracion',
  FORMATO_TRANSFERENCIA = 'formato_transferencia',
  CARTA_REEMPLAZO = 'carta_reemplazo',
  FACTURA_INVALIDA = 'factura_invalida',
  CREDENCIALES_PORTAL = 'credenciales_portal',
  CAMBIO_DE_CUENTA = 'cambio_de_cuenta',
  REQUIERE_ATENCION = 'requiere_atencion',

  // PAGADO (5)
  CONFIRMAR_PAGO = 'confirmar_pago',
  PENDIENTE_COMPLEMENTO = 'pendiente_complemento',
  SUBIR_COMPLEMENTO = 'subir_complemento',
  PENDIENTE_SUBIR_ASEGURADORA = 'pendiente_subir_aseguradora',
  FINALIZADO = 'finalizado',
}

// ============================================
// ESTADO DE COMISIÓN
// ============================================
export enum CommissionStatus {
  EN_PROCESO = 'en_proceso',
  PENDIENTE = 'pendiente',
  PAGADA = 'pagada',
  CANCELADA = 'cancelada',
}

// ============================================
// TIPO DE SERVICIO
// ============================================
export enum ServiceType {
  COLLECTION_ONLY = 'collection_only',
  INVOICING_PLATFORM = 'invoicing_platform',
  INVOICING_EXTERNAL_PORTAL = 'invoicing_external_portal',
}

// ============================================
// ESTADO SAT DE FACTURA
// ============================================
export enum InvoiceSatStatus {
  VIGENTE = 'vigente',
  CANCELACION_SOLICITADA = 'cancelacion_solicitada',
  PENDIENTE_SAT = 'pendiente_sat',
  PENDIENTE_ASEGURADORA = 'pendiente_aseguradora',
  CANCELADA = 'cancelada',
  RECHAZADA = 'rechazada',
}

/**
 * Service Type (legacy alias for backward compatibility)
 * @deprecated Use ServiceType enum instead
 */
export type TaskServiceType =
  | 'collection_only'
  | 'invoicing_platform'
  | 'invoicing_external_portal';

/**
 * Payment Status
 * @deprecated Use CommissionStatus enum instead
 */
export type TaskPaymentStatus = 'pending' | 'partial' | 'received' | 'paid' | 'failed';

// Export TaskType from constants
export type { TaskType };

/**
 * Main Task Entity (FSM v2.0)
 */
export interface Task {
  id: string;
  doctorId: string;
  insurerId: string | null;

  // Datos de la Carta de Autorización
  patientName: string | null;
  claimNumber: string | null;

  description: string;
  type: TaskType | null;

  // FSM v2.0 - Estado jerárquico
  status: TaskStatus;
  subStatus: TaskSubStatus | null;

  // Servicio y facturación
  serviceType: ServiceType;
  requiresAlta: boolean;
  altaFee?: string | null;
  altaStatus?: 'pending' | 'completed' | null;
  altaCompletedDate?: Date | null;

  // Montos
  amount: string;
  commissionRate: string;
  commissionAmount: string | null;

  // Estado de comisión
  commissionStatus: CommissionStatus;
  commissionBilled: boolean;
  commissionPaid: boolean;

  // Pago
  paymentReceivedDate: Date | null;
  paymentReceivedAmount: string | null;
  paymentReference: string | null;
  paymentProofUrl: string | null;
  paymentProofS3Key: string | null;

  // Payment Complement
  paymentComplementRequired: boolean;
  paymentComplementAmount: string | null;
  paymentComplementCfdiUuid: string | null;
  paymentComplementUploaded: boolean;
  paymentComplementGeneratedDate: Date | null;
  complementUploadedToInsurerAt: Date | null;
  complementInsurerFolio: string | null;

  // Fechas
  submittedToInsurerDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  completedAt: Date | null;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;

  // Notas
  notes?: string;
  insurerNotes?: string;

  // Factura relacionada
  invoiceId?: string;
  invoiceSatStatus?: InvoiceSatStatus;
  invoiceSerie?: string | null; // Folio/Serie de la factura
  invoiceIssuedDate?: Date | null; // Fecha de emisión de la factura

  // Fecha probable de pago (calculada: +25 días de subida a aseguradora)
  expectedPaymentDate?: Date | null;

  // Submission to insurer (folio tracking)
  uploadFolio?: string | null;
  uploadedToInsurerAt?: Date | null;

  // Commission paid date (when status = pagada)
  commissionPaidDate?: Date | null;

  // Insurer-specific fields (2025-12-30)
  invoiceFolio?: string | null; // Folio de factura (ej: A052)
  invoiceUuidProvided?: string | null; // UUID CFDI de la factura del doctor
  invoicePdfProvidedId?: string | null; // ID del documento PDF de factura
  invoiceXmlProvidedId?: string | null; // ID del documento XML de factura
  policyNumber?: string | null; // Mapfre/Metlife
  ramFolio?: string | null; // Mapfre
  bpmFolio?: string | null; // Mapfre
  axaAttentionNumber?: string | null; // AXA
  axaAuthorizationFolio?: string | null; // AXA
  axaDictamen?: string | null; // AXA
  cpt?: string | null; // Banorte

  // Relaciones expandidas desde API
  doctorName?: string;
  doctorEmail?: string;
  insurerName?: string;

  // Información del doctor (para transiciones)
  doctor?: {
    id: string;
    name: string;
    serviceType: ServiceType;
    rfc?: string | null;
  };
  // Detención
  detainedReason?: string | null;
  detainedDescription?: string | null;
  detainedDate?: Date | null;
  detainedResolvedDate?: Date | null;

  // RFC del doctor (denormalized from doctor relation)
  doctorRfc?: string | null;
}

/**
 * Task Create Input
 */
export interface TaskCreateInput {
  doctorId: string;
  insurerId?: string;
  patientName?: string;
  claimNumber?: string;
  description: string;
  type?: TaskType;
  serviceType?: ServiceType;
  amount: string;
  commissionRate: string;
  status?: TaskStatus;
  subStatus?: TaskSubStatus;
  startDate?: string;
  endDate?: string;
  requiresAlta?: boolean;
  altaFee?: string;
}

/**
 * Task Update Input
 */
export interface TaskUpdateInput {
  doctorId?: string;
  insurerId?: string;
  patientName?: string;
  claimNumber?: string;
  description?: string;
  type?: TaskType;
  serviceType?: ServiceType;
  status?: TaskStatus;
  subStatus?: TaskSubStatus;
  amount?: string;
  commissionRate?: string;
  commissionStatus?: CommissionStatus;
  paymentReceivedDate?: string;
  paymentReceivedAmount?: string;
  paymentReference?: string;
  paymentProofUrl?: string;
  paymentProofS3Key?: string;
  commissionBilled?: boolean;
  commissionPaid?: boolean;
  submittedToInsurerDate?: string;
  startDate?: string;
  endDate?: string;
  completedAt?: string;
  requiresAlta?: boolean;
  altaFee?: string;
  expectedPaymentDate?: string;
  // Invoice fields
  invoiceFolio?: string;
  invoiceSerie?: string;
  invoiceIssuedDate?: Date;
  invoicePdfProvidedId?: string;
  invoiceXmlProvidedId?: string;
  invoiceUuidProvided?: string;
  // Insurer-specific fields (2025-12-30)
  policyNumber?: string;
  ramFolio?: string;
  bpmFolio?: string;
  axaAttentionNumber?: string;
  axaAuthorizationFolio?: string;
  axaDictamen?: string;
  cpt?: string;
  // Upload tracking fields
  uploadFolio?: string | null;
  uploadedToInsurerAt?: string | null;
  // Notes fields
  notes?: string;
  insurerNotes?: string;
}

/**
 * Task File Attachment
 */
export interface TaskFile {
  id: string;
  taskId: string;
  fileUrl: string;
  s3Key: string | null;
  description: string | null;
  createdAt: Date;
  deletedAt: Date | null;
}

/**
 * Task File Create Input
 */
export interface TaskFileCreateInput {
  taskId: string;
  fileUrl: string;
  s3Key?: string;
  description?: string;
}

/**
 * Task Sort Field
 */
export type TaskSortField =
  | 'createdAt'
  | 'startDate'
  | 'amount'
  | 'status'
  | 'subStatus'
  | 'commissionStatus'
  | 'doctorName';

/**
 * Sort Order
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Task List Filters
 */
export interface TaskFilters {
  page?: number;
  limit?: number;
  doctorId?: string;
  insurerId?: string | string[];
  status?: TaskStatus | TaskStatus[];
  subStatus?: TaskSubStatus | TaskSubStatus[];
  commissionStatus?: CommissionStatus | CommissionStatus[];
  serviceType?: ServiceType | ServiceType[];
  fromDate?: string;
  toDate?: string;
  search?: string;
  sortBy?: TaskSortField;
  sortOrder?: SortOrder;
  // Amount range filters
  amountMin?: number | string;
  amountMax?: number | string;
  // Upload date range filters
  uploadedFromDate?: string;
  uploadedToDate?: string;
  // Expected payment status filter
  expectedPaymentStatus?:
    | 'overdue'
    | 'due_this_week'
    | 'due_next_week'
    | 'due_this_month'
    | ('overdue' | 'due_this_week' | 'due_next_week' | 'due_this_month')[];
  // Document status filter
  documentStatus?:
    | 'has_invoice'
    | 'no_invoice'
    | 'has_proof'
    | 'no_proof'
    | 'pending_docs'
    | ('has_invoice' | 'no_invoice' | 'has_proof' | 'no_proof' | 'pending_docs')[];
}

// ============================================
// HISTORIAL DE TRANSICIONES
// ============================================

/**
 * Entrada en el historial de transiciones del trámite
 */
export interface TaskStatusHistoryEntry {
  id: string;
  taskId: string;
  fromStatus: TaskStatus | null;
  fromSubStatus: TaskSubStatus | null;
  toStatus: TaskStatus;
  toSubStatus: TaskSubStatus | null;
  notes?: string | null;
  reason?: string | null;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
}

// ============================================
// TRANSICIONES FSM
// ============================================

/**
 * Información de una transición disponible
 */
export interface TransitionInfo {
  toStatus: TaskStatus;
  toSubStatus: TaskSubStatus | null;
  label: string;
  description: string;
  requiresConfirmation?: boolean;
  confirmationMessage?: string;
}

/**
 * Request para realizar una transición
 */
export interface TransitionRequest {
  taskId: string;
  toStatus: TaskStatus;
  toSubStatus?: TaskSubStatus | null;
  notes?: string;
  paymentAmount?: string;
  paymentReference?: string;
  paymentDate?: string;
}

/**
 * Response de transiciones disponibles desde backend
 */
export interface AvailableTransitionsResponse {
  currentStatus: TaskStatus;
  currentSubStatus: TaskSubStatus | null;
  availableTransitions: TransitionInfo[];
}

// ============================================
// ESTADÍSTICAS GLOBALES DE TRÁMITES
// ============================================

/**
 * Stats for a single task status
 */
export interface TaskStatusStats {
  count: number;
  amount: string;
}

/**
 * Global tasks stats by status
 * Response from GET /api/tasks/stats
 */
export interface GlobalTasksStats {
  byStatus: {
    verificacion: TaskStatusStats;
    en_proceso: TaskStatusStats;
    detenidos: TaskStatusStats;
    pagado: TaskStatusStats;
    negados: TaskStatusStats;
    cancelado: TaskStatusStats;
  };
  totals: {
    totalTasks: number;
    totalAmount: string;
  };
}
